import * as React from 'react';
import styled from 'styled-components';
import {TscStyles, TscThemeName, TscThemeNames, Spinner} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import {StyledAnchor} from '../util/StyledElements';
import {conversationsStore} from '../../context/conversationsProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';

const ieMediaHackOffset = 200;
export const Table = styled.ul<{breakpoint: number}>`
   list-style-type: none;
   min-height: 5rem;
   display: grid;
   grid-template-columns: repeat( auto-fit, minmax(14rem, 1fr) );
   grid-gap: 1rem;
   padding: 0;

   @media screen and (min-width: ${props => props.breakpoint}px){
     grid-gap: 0;
     display: block;
   }

   // IE 11 hax
   @media screen and (min-width: ${props => props.breakpoint}px) and (-ms-high-contrast: none) {
      column-count: auto;
   }

   // IE 11 hax
   @media screen and (max-width: ${props => props.breakpoint}px) and (-ms-high-contrast: none) {
      column-count: 2;
   }

   // IE 11 hax
   @media screen and (max-width: ${props => props.breakpoint - ieMediaHackOffset}px) and (-ms-high-contrast: none) {
      column-count: 1;
   }
`;

const TableWrapper = styled.div<{theme: TscThemeNames; breakpoint: number}>`
   margin-top: 1rem;

   @media screen and (min-width: ${props => props.breakpoint}px){
      background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.lightMedium};
      border: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : TscStyles.color.ui.dawn.medium};
      padding: .5rem 1rem;
      border-radius: 4px;
      color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
   }
`;

const TableItem = styled.li<{theme: TscThemeNames; breakpoint: number}>`
   padding: .5rem 0;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : TscStyles.color.ui.dawn.medium};
   padding: .5rem 1rem;
   border-radius: 4px;
   margin-right: 0;
   align-items: center;

   @media screen and (min-width: ${props => props.breakpoint}px){
      display: grid;
      background: transparent;
      width: 100%;
      border: none;
      padding-left: 0;
      padding-right: 0;
      grid-template-columns: 2fr repeat(3, 1fr) 2rem;
   }

   // IE 11 hax
   @media screen and (min-width: ${props => props.breakpoint}px) and (-ms-high-contrast: none) {
      display: flex;
      width: 100%;
      justify-content: space-between;
      box-sizing: border-box;
   }

   // IE 11 hax
   @media screen and (max-width: ${props => props.breakpoint}px) and (-ms-high-contrast: none) {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: flex-start;
      align-items: flex-start;
      box-sizing: border-box;
      overflow: hidden;
      margin-bottom: 1rem;

      & > div {
         padding-bottom: 0.75rem;
         display: flex;
         justify-content: center;
         align-items: center;
      }
   }
`;

const TableHeaderItem = styled.div<{theme: TscThemeNames; breakpoint: number}>`
   padding: .5rem 0;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : TscStyles.color.ui.dawn.medium};
   padding: .5rem 1rem;
   border-radius: 4px;
   margin-right: 0;
   align-items: center;
   display: none;
   font-weight: 600;
   text-transform: uppercase;
   font-size: .75rem;

   @media screen and (min-width: ${props => props.breakpoint}px){
      display: grid;
      background: transparent;
      width: 100%;
      border: none;
      padding-left: 0;
      padding-right: 0;
      grid-template-columns: 2fr repeat(3, 1fr) 2rem;
   }

   // IE 11 hax
   @media screen and (min-width: ${props => props.breakpoint}px) and (-ms-high-contrast: none) {
      display: flex;
      width: 80%;
      justify-content: space-around;
   }
`;

const ItemCell = styled.div<{theme: TscThemeNames; breakpoint: number}>`
   display: grid;
   grid-template-columns: 7rem 2fr;
   grid-gap: .5rem;
   align-items: center;

   a {
      color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      margin-right: 1rem;
   }

   span {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
   }

   &:before {
     margin-right: 1rem;
     display: block;
     font-weight: 600;
     text-transform: uppercase;
     font-size: .75rem;

     @media screen and (min-width: ${props => props.breakpoint}px){
       display: none;
     }
   }

   @media screen and (min-width: ${props => props.breakpoint}px){
      grid-template-columns: none;
   }

   @media screen and (max-width: ${props => props.breakpoint}px){
      margin-bottom: 0.5rem;
   }
`;

const SmallViewLabel = styled.div<{breakpoint: number}>`
   margin-right: 1rem;
   display: block;
   font-weight: 600;
   text-transform: uppercase;
   font-size: .75rem;

   @media screen and (min-width: ${props => props.breakpoint}px){
      display: none;
   }
`;

export const EmptyData = styled(TableItem)`
   grid-template-columns: none !important;
   grid-template: none !important;
   text-align: center;
   font-style: italic;
   padding-top: 1.75rem;
`;

export const DataTableBase: React.FC<IDataTableProps & IStateMappedProps> = ({columnLabels, tableRows, emptyStateLabel, conversationPaneOpen, isLoading = false, theme}) => {
   const breakpoint = cssConstants.tableBreakpoint(conversationPaneOpen);

   const createCell = (rowItem: string|React.JSX.Element|number, colIndex: number, link?: string) => (
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      <ItemCell key={`${rowItem}-${colIndex}`} theme={theme} breakpoint={breakpoint}>
         <SmallViewLabel breakpoint={breakpoint}>{columnLabels[colIndex]}</SmallViewLabel>
         {
            colIndex === 0 && link ?
               <StyledAnchor href={link}>{rowItem}</StyledAnchor> :
               typeof rowItem === 'number' || typeof rowItem === 'string' ? <span>{rowItem}</span> : rowItem
         }
      </ItemCell>
   );

   const createRow = (row: ITableRowContent, rowIndex: number) => (
      <TableItem key={rowIndex} theme={theme} breakpoint={breakpoint} className="data-table-row">
         {row.tableRow.map((rowItem: string|React.JSX.Element|number, index: number) => createCell(rowItem, index, row.link))}
         {row.button}
      </TableItem>
   );

   const LoadingState =
      (<TableWrapper theme={theme} breakpoint={breakpoint}>
         <Table breakpoint={breakpoint}>
            <EmptyData theme={theme} breakpoint={breakpoint}><Spinner themeName={theme}/></EmptyData>
         </Table>
      </TableWrapper>);

   const emptyState =
      (<TableWrapper theme={theme} breakpoint={breakpoint}>
         <Table breakpoint={breakpoint}>
            <EmptyData theme={theme} breakpoint={breakpoint}>{emptyStateLabel}</EmptyData>
         </Table>
      </TableWrapper>);

   const nonEmptyState =
      (<TableWrapper theme={theme} breakpoint={breakpoint}>
         <TableHeaderItem theme={theme} breakpoint={breakpoint} className="data-table-header">
            {columnLabels.map((label: string, index: number) => <div key={`${label}-${index}`}>{label}</div>)}
         </TableHeaderItem>
         <Table breakpoint={breakpoint}>
            {tableRows.map((row: ITableRowContent, index: number) => createRow(row, index))}
         </Table>
      </TableWrapper>);

   return isLoading ? LoadingState : !tableRows.length ? emptyState : nonEmptyState;
};

export interface IDataTableProps {
   columnLabels: string[];
   emptyStateLabel: string;
   tableRows: ITableRowContent[];
   isLoading?: boolean;
}

export interface ITableRowContent {
   tableRow: (string|React.JSX.Element|number)[];
   link?: string;
   button?: React.ReactElement;
}

export interface IStateMappedProps {
   conversationPaneOpen: boolean;
   theme: TscThemeNames;
}

const mapStatesToProps = (
   dataTableProps: IDataTableProps,
   {conversationPaneOpen}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>
): IDataTableProps & IStateMappedProps => ({theme, conversationPaneOpen, ...dataTableProps});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, themeStore)(DataTableBase);
